import { AreaSwitcher } from '~/shell/components/AreaSwitcher';
import { Link } from 'react-router';
import { LanguageSwitcher } from '~/shell/components/LanguageSwitcher';
import ProdegaLogo from '~/assets/logos/prodega-logo';
import TransgourmetLogo from '~/assets/logos/transgourmet-logo';
import { CartButton } from '~/shell/components/CartButton';
import { HeaderNavigation } from '~/shell/components/HeaderNavigation';
import { ShopLocationSelector } from '~/shell/components/ShopLocationSelector';
import type { Area } from '~/common/types/area';
import { PgArea, TgArea } from '~/common/types/area';
import { useBasePath } from '~/common/hooks/useBasePath';
import { Help } from '~/shell/components/Help';
import { Notifications } from '~/shell/components/Notifications';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { cn } from '~/common/utils/styleUtil';
import { useWebshopMessages } from '~/features/webshopmessages/hooks/useWebshopMessages';
import { UnauthorizedCartButton } from '~/shell/components/UnauthorizedCartButton';
import { DesktopQuickSearch } from '~/features/catalog/components/quicksearch/DesktopQuickSearch';
import type { IWebshopLoginInfoDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { KundencenterTel } from '~/shell/components/KundencenterTel.tsx';
import { TrackNavigationClick } from '~/tracking/TrackNavigationClick';

export interface DesktopHeaderProps {
  area: Area;
  loginInfo?: IWebshopLoginInfoDtoData;
  isOciSession?: boolean;
  children?: ReactNode;
  className?: string;
}

export function DesktopHeader({ area, loginInfo, children, className, isOciSession }: Readonly<DesktopHeaderProps>) {
  const { path } = useBasePath();
  const { setIsDialogOpen, showNotificationBell } = useWebshopMessages();
  const [isDesktopQuickSearchOpen, setIsDesktopQuickSearchOpen] = useState(false);

  return (
    <div className={className}>
      {children && <div>{children}</div>}
      <div className="bg-muted h-[36px]">
        <div className="2xl:container 2xl:mx-auto flex justify-between items-center h-full px-8">
          <div className={'flex gap-4'}>
            <AreaSwitcher />
            {area === PgArea && <ShopLocationSelector />}
          </div>
          <div className={'flex items-center text-secondary'}>
            {showNotificationBell && <Notifications onOpen={() => setIsDialogOpen(true)} />}
            <KundencenterTel />
            <Help isAgh={area === PgArea} />
            <LanguageSwitcher />
          </div>
        </div>
      </div>
      <div className="2xl:container 2xl:mx-auto flex flex-row items-center h-[80px] gap-8 px-8">
        <div className="flex flex-row items-center w-[260px]">
          <TrackNavigationClick label={'Home'}>
            <Link to={path} className={cn('w-[240px]', area === PgArea && 'w-[180px]')} data-testid={'home-link'}>
              {area === PgArea ? <ProdegaLogo /> : <TransgourmetLogo />}
            </Link>
          </TrackNavigationClick>
        </div>
        <div className="grow flex flex-row items-center">
          <DesktopQuickSearch area={area} onOpenChange={setIsDesktopQuickSearchOpen} isAuthenticated={!!loginInfo} />
        </div>
        <div
          className={cn(
            `transition-all duration-300 overflow-hidden ease-in-out block`,
            isDesktopQuickSearchOpen && 'hidden xl:block xl:invisible'
          )}
        >
          <div className="flex flex-col items-end">
            <div className="flex justify-end items-center gap-2">
              <HeaderNavigation loginInfo={loginInfo} isOciSession={isOciSession} />
              {!loginInfo && area === TgArea && <UnauthorizedCartButton />}
              {area === TgArea && (loginInfo?.customerType === 'BGH' || loginInfo?.customerType === 'MULTICHANNEL') ? (
                <CartButton />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
